import { useCallback, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';

import Iconify from 'src/components/iconify';
import ProjectUpload from 'src/components/upload/project-upload';
import { useBoolean } from 'src/hooks/use-boolean';
import axiosInstance from 'src/utils/axios';
import { trimPdfClient } from 'src/utils/trimPdfClient';

import LoaderDialog from './project-loader-dialog';
import ProjectReviewDialog from './project-review-dialog';

const UPLOAD_STATUS = {
  IDLE: 'idle',
  UPLOADING: 'uploading',
  SUCCESS: 'success',
  ERROR: 'error',
};

export function CreateProjectDialog({ dialog, setOpenDrawer, setExtractedData }) {
  const loaderDialog = useBoolean(false);
  const reviewDialog = useBoolean(false);

  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(UPLOAD_STATUS.IDLE);
  const [localExtractedData, setLocalExtractedData] = useState(null);

  // -----------------------------
  // Helpers
  // -----------------------------
  const resetUploadState = useCallback(() => {
    setFile(null);
    setUploadProgress(0);
    setUploadStatus(UPLOAD_STATUS.IDLE);
    setExtractedData(null);
    setLocalExtractedData(null);
  }, [setExtractedData]);

  // -----------------------------
  // Upload logic
  // -----------------------------
  const uploadProjectPdf = useCallback(async (pdfFile) => {
    const trimmedFile = await trimPdfClient(pdfFile, 5);

    const formData = new FormData();
    formData.append('file', trimmedFile);

    const response = await axiosInstance.post('/api/project/upload-project-file', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (e) => {
        if (!e.total) return;

        // Cap upload progress at 90%, final 10% reserved for processing
        const percent = Math.min(Math.round((e.loaded / e.total) * 90), 90);

        setUploadProgress((prev) => (percent > prev ? percent : prev));
      },
    });

    return response.data.data;
  }, []);

  const handleProjectUpload = useCallback(
    async (newFile) => {
      if (!newFile) return;

      try {
        setFile(newFile);
        setUploadStatus(UPLOAD_STATUS.UPLOADING);
        setUploadProgress(0);

        const project = await uploadProjectPdf(newFile);

        setUploadProgress(100);
        setUploadStatus(UPLOAD_STATUS.SUCCESS);
        setExtractedData(project);
        setLocalExtractedData(project);
      } catch (error) {
        console.error('Project upload failed:', error);
        setUploadStatus(UPLOAD_STATUS.ERROR);
      }
    },
    [uploadProjectPdf, setExtractedData]
  );

  // -----------------------------
  // Render
  // -----------------------------
  return (
    <>
      <Dialog
        open={!loaderDialog.value && !reviewDialog.value}
        onClose={dialog.onFalse}
        sx={{
          '& .MuiPaper-root.MuiPaper-elevation.MuiPaper-rounded.MuiPaper-elevation24.MuiDialog-paper.MuiDialog-paperScrollPaper.MuiDialog-paperWidthSm':
            {
              minWidth: { xs: '100%', sm: 500 },
              px: 1,
              pb: 2,
            },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: 20,
            fontWeight: 700,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2.5,
          }}
        >
          Upload Plan (AI-Assisted)
          <Iconify
            icon="material-symbols:close-small-outline"
            height={32}
            width={32}
            sx={{ cursor: 'pointer' }}
            onClick={dialog.onFalse}
          />
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            px: 2,
          }}
        >
          <ProjectUpload
            file={file}
            uploadProgress={uploadProgress}
            uploadStatus={uploadStatus}
            onUpload={handleProjectUpload}
            onRetry={handleProjectUpload}
            onDelete={resetUploadState}
          />

          {uploadStatus === UPLOAD_STATUS.SUCCESS && file && (
            <Button
              fullWidth
              variant="contained"
              onClick={loaderDialog.onTrue}
              sx={{
                py: 1.5,
                backgroundColor: '#212B36',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <img src="/assets/icons/upload/upload.svg" alt="upload" />
              <Typography fontSize={14} fontWeight={700} color="#fff">
                Create Project
              </Typography>
            </Button>
          )}

          <Divider>OR</Divider>

          <Typography
            align="center"
            fontSize={14}
            color="text.secondary"
            sx={{
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
            }}
            onClick={() => {
              resetUploadState();
              setOpenDrawer(true);
              dialog.onFalse();
            }}
          >
            <Box component="span" sx={{ color: 'primary.main' }}>
              Create{' '}
            </Box>
            Project Manually
          </Typography>
        </DialogContent>
      </Dialog>

      {loaderDialog.value && (
        <LoaderDialog
          dialog={loaderDialog}
          onClose={() => {
            loaderDialog.onFalse();
            reviewDialog.onTrue();
          }}
        />
      )}

      {reviewDialog.value && (
        <ProjectReviewDialog
          dialog={reviewDialog}
          extractedData={localExtractedData}
          onBack={() => {
            // Go back to upload dialog
            reviewDialog.onFalse();
          }}
          onConfirm={(updated) => {
            setExtractedData(updated);
            setLocalExtractedData(updated);
            reviewDialog.onFalse();
            dialog.onFalse();
            setOpenDrawer(true);
          }}
        />
      )}
    </>
  );
}

CreateProjectDialog.propTypes = {
  dialog: PropTypes.object.isRequired,
  setOpenDrawer: PropTypes.func.isRequired,
  setExtractedData: PropTypes.func.isRequired,
};
